import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, TextField, Button, Grid, MenuItem, Paper,
  Divider, Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, FormControl, InputLabel, Select, Alert
} from '@mui/material';
import { Print, PersonAdd, Save } from '@mui/icons-material';
import { useReactToPrint } from 'react-to-print';
import { toast } from 'react-toastify';
import api from '../services/api';

const AdmissionForm = () => {
  const printRef = useRef();
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [enrollDialog, setEnrollDialog] = useState(false);
  const [enrollClass, setEnrollClass] = useState('');
  const [enrollSection, setEnrollSection] = useState('');
  const [yearStart, setYearStart] = useState('2025');
  const [yearEnd, setYearEnd] = useState('26');
  const [formNo, setFormNo] = useState('000001');

  const [form, setForm] = useState({
    surname: '', firstName: '', middleName: '',
    fatherSurname: '', fatherFirstName: '', fatherMiddleName: '',
    motherName: '',
    religion: '', caste: '', subCaste: '', category: '',
    dobDate: '', dobMonth: '', dobYear: '', dobWords: '',
    birthPlace: '', birthTaluka: '', birthDistrict: '',
    aadharNo: '', bloodGroup: '',
    motherTongue: '', nationality: 'Indian',
    previousSchool: '', previousBoard: '',
    parentName: '', parentAddress: '', contactNo: '', altContact: '',
    classAdmission: '',
    fatherOccupation: '', fatherQualification: '', fatherMobile: '', fatherEmail: '', fatherAddress: '',
    motherOccupation: '', motherQualification: '', motherMobile: '', motherEmail: '', motherAddress: '',
    illness: '',
  });

  const handlePrint = useReactToPrint({ contentRef: printRef });

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const res = await api.get('/classes');
        setClasses(res.data.data || []);
      } catch (e) { console.error(e); }
    };
    fetchClasses();
  }, []);

  const handleClassChange = async (classId) => {
    setEnrollClass(classId);
    setEnrollSection('');
    try {
      const res = await api.get(`/classes/${classId}/sections`);
      setSections(res.data.data || []);
    } catch (e) { console.error(e); }
  };

  const handleEnroll = async () => {
    if (!enrollClass || !enrollSection) {
      toast.warning('Select class and section'); return;
    }
    setLoading(true);
    try {
      const fullName = `${form.surname} ${form.firstName} ${form.middleName}`.trim();
      await api.post('/students', {
        firstName: form.firstName || form.surname,
        lastName: form.surname,
        middleName: form.middleName,
        classId: enrollClass,
        sectionId: enrollSection,
        dateOfBirth: form.dobYear && form.dobMonth && form.dobDate
          ? `${form.dobYear}-${form.dobMonth.padStart(2,'0')}-${form.dobDate.padStart(2,'0')}` : undefined,
        gender: '',
        fatherName: `${form.fatherSurname} ${form.fatherFirstName} ${form.fatherMiddleName}`.trim(),
        motherName: form.motherName,
        phone: form.contactNo || form.fatherMobile,
        religion: form.religion,
        caste: form.caste,
        subCaste: form.subCaste,
        category: form.category,
        uid: form.aadharNo,
        bloodGroup: form.bloodGroup,
        motherTongue: form.motherTongue,
        nationality: form.nationality,
        placeOfBirth: form.birthPlace,
        previousSchool: form.previousSchool,
        address: form.parentAddress,
      });
      toast.success(`Student ${fullName} enrolled successfully!`);
      setEnrollDialog(false);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to enroll student');
    } finally { setLoading(false); }
  };

  const u = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  // Underline field style for print
  const uf = { borderBottom: '1px solid #000', minWidth: 80, display: 'inline-block', px: 0.5, fontWeight: 'bold', fontSize: 11 };

  return (
    <Box>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography variant="h5" fontWeight={700} sx={{ flexGrow: 1 }}>Admission Form</Typography>
        <TextField size="small" label="Year Start" value={yearStart} onChange={e => setYearStart(e.target.value)} sx={{ width: 90 }} />
        <TextField size="small" label="Year End" value={yearEnd} onChange={e => setYearEnd(e.target.value)} sx={{ width: 70 }} />
        <TextField size="small" label="Form No" value={formNo} onChange={e => setFormNo(e.target.value)} sx={{ width: 100 }} />
        <Button variant="contained" startIcon={<Print />} onClick={handlePrint} sx={{ bgcolor: '#2196f3' }}>Print</Button>
        <Button variant="contained" startIcon={<PersonAdd />} onClick={() => setEnrollDialog(true)}
          sx={{ background: 'linear-gradient(135deg,#4caf50,#2e7d32)' }}>Enroll Student</Button>
      </Box>

      {/* Editable Form Fields */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} color="#1a9e8f" gutterBottom>Student Information</Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={4}><TextField fullWidth size="small" label="Surname" value={form.surname} onChange={u('surname')} /></Grid>
          <Grid item xs={4}><TextField fullWidth size="small" label="First Name" value={form.firstName} onChange={u('firstName')} /></Grid>
          <Grid item xs={4}><TextField fullWidth size="small" label="Middle Name" value={form.middleName} onChange={u('middleName')} /></Grid>
          <Grid item xs={4}><TextField fullWidth size="small" label="Father Surname" value={form.fatherSurname} onChange={u('fatherSurname')} /></Grid>
          <Grid item xs={4}><TextField fullWidth size="small" label="Father Name" value={form.fatherFirstName} onChange={u('fatherFirstName')} /></Grid>
          <Grid item xs={4}><TextField fullWidth size="small" label="Father's Father Name" value={form.fatherMiddleName} onChange={u('fatherMiddleName')} /></Grid>
          <Grid item xs={4}><TextField fullWidth size="small" label="Mother's Name" value={form.motherName} onChange={u('motherName')} /></Grid>
          <Grid item xs={2}><TextField fullWidth size="small" label="Religion" value={form.religion} onChange={u('religion')} /></Grid>
          <Grid item xs={3}><TextField fullWidth size="small" label="Caste/Sub Caste" value={form.caste} onChange={u('caste')} /></Grid>
          <Grid item xs={3}><TextField fullWidth size="small" label="Category" value={form.category} onChange={u('category')} /></Grid>
          <Grid item xs={2}><TextField fullWidth size="small" label="DOB Date" value={form.dobDate} onChange={u('dobDate')} /></Grid>
          <Grid item xs={2}><TextField fullWidth size="small" label="DOB Month" value={form.dobMonth} onChange={u('dobMonth')} /></Grid>
          <Grid item xs={2}><TextField fullWidth size="small" label="DOB Year" value={form.dobYear} onChange={u('dobYear')} /></Grid>
          <Grid item xs={6}><TextField fullWidth size="small" label="DOB in Words" value={form.dobWords} onChange={u('dobWords')} /></Grid>
          <Grid item xs={4}><TextField fullWidth size="small" label="Place of Birth" value={form.birthPlace} onChange={u('birthPlace')} /></Grid>
          <Grid item xs={4}><TextField fullWidth size="small" label="Taluka" value={form.birthTaluka} onChange={u('birthTaluka')} /></Grid>
          <Grid item xs={4}><TextField fullWidth size="small" label="District" value={form.birthDistrict} onChange={u('birthDistrict')} /></Grid>
          <Grid item xs={4}><TextField fullWidth size="small" label="Aadhar No" value={form.aadharNo} onChange={u('aadharNo')} /></Grid>
          <Grid item xs={2}><TextField fullWidth size="small" label="Blood Group" value={form.bloodGroup} onChange={u('bloodGroup')} /></Grid>
          <Grid item xs={3}><TextField fullWidth size="small" label="Mother Tongue" value={form.motherTongue} onChange={u('motherTongue')} /></Grid>
          <Grid item xs={3}><TextField fullWidth size="small" label="Nationality" value={form.nationality} onChange={u('nationality')} /></Grid>
          <Grid item xs={6}><TextField fullWidth size="small" label="Previous School & Board" value={form.previousSchool} onChange={u('previousSchool')} /></Grid>
          <Grid item xs={3}><TextField fullWidth size="small" label="Class Sought" value={form.classAdmission} onChange={u('classAdmission')} /></Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" fontWeight={700} color="#1a9e8f" gutterBottom>Parent / Guardian</Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={6}><TextField fullWidth size="small" label="Parent Name" value={form.parentName} onChange={u('parentName')} /></Grid>
          <Grid item xs={6}><TextField fullWidth size="small" label="Address" value={form.parentAddress} onChange={u('parentAddress')} /></Grid>
          <Grid item xs={3}><TextField fullWidth size="small" label="Contact No" value={form.contactNo} onChange={u('contactNo')} /></Grid>
          <Grid item xs={3}><TextField fullWidth size="small" label="Alt Contact" value={form.altContact} onChange={u('altContact')} /></Grid>
          <Grid item xs={3}><TextField fullWidth size="small" label="Father Occupation" value={form.fatherOccupation} onChange={u('fatherOccupation')} /></Grid>
          <Grid item xs={3}><TextField fullWidth size="small" label="Father Mobile" value={form.fatherMobile} onChange={u('fatherMobile')} /></Grid>
          <Grid item xs={3}><TextField fullWidth size="small" label="Mother Occupation" value={form.motherOccupation} onChange={u('motherOccupation')} /></Grid>
          <Grid item xs={3}><TextField fullWidth size="small" label="Mother Mobile" value={form.motherMobile} onChange={u('motherMobile')} /></Grid>
        </Grid>
      </Paper>

      {/* ============== PRINTABLE FORM ============== */}
      <Paper sx={{ p: 0, overflow: 'hidden' }}>
        <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderBottom: '1px solid #e0e0e0', display: 'flex', gap: 1 }}>
          <Button variant="contained" size="small" startIcon={<Print />} onClick={handlePrint} sx={{ bgcolor: '#2196f3' }}>Print Form</Button>
          <Button variant="outlined" size="small" color="error" onClick={() => setForm({
            surname:'',firstName:'',middleName:'',fatherSurname:'',fatherFirstName:'',fatherMiddleName:'',
            motherName:'',religion:'',caste:'',subCaste:'',category:'',dobDate:'',dobMonth:'',dobYear:'',
            dobWords:'',birthPlace:'',birthTaluka:'',birthDistrict:'',aadharNo:'',bloodGroup:'',
            motherTongue:'',nationality:'Indian',previousSchool:'',previousBoard:'',parentName:'',
            parentAddress:'',contactNo:'',altContact:'',classAdmission:'',fatherOccupation:'',
            fatherQualification:'',fatherMobile:'',fatherEmail:'',fatherAddress:'',motherOccupation:'',
            motherQualification:'',motherMobile:'',motherEmail:'',motherAddress:'',illness:'',
          })}>Clear All</Button>
        </Box>

        <Box sx={{ p: 3, bgcolor: '#f5f5f5' }}>
          <Box ref={printRef} sx={{
            background: '#fff', maxWidth: 800, mx: 'auto', p: '15mm',
            fontFamily: '"Times New Roman", serif', fontSize: 12, color: '#000',
            '@media print': { p: '10mm', maxWidth: 'none' }
          }}>
            {/* PAGE 1: ADMISSION FORM */}
            {/* School Header */}
            <Box sx={{ textAlign: 'center', mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
              <Box component="img" src="/logo1.png" alt="Logo" sx={{ width: 60, height: 60, objectFit: 'contain' }} />
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 'bold', letterSpacing: 1 }}>VIDYA PRATISHTHAN'S</Typography>
                <Typography sx={{ fontSize: 18, fontWeight: 'bold', letterSpacing: 2 }}>SUPE ENGLISH MEDIUM SCHOOL</Typography>
                <Typography sx={{ fontSize: 9 }}>A/p- Supe Tal-Baramati Dist. Pune Pin-412204 Approval No.- 890 / Dyde – Pune -16798 – 27/07/2016</Typography>
                <Typography sx={{ fontSize: 9 }}>Udise No.27250200605 Website-www.vpsems.org ISO-9001:2015 Email-sems@vidyapratishthan.com Mo.7796686110</Typography>
              </Box>
            </Box>

            <Divider sx={{ borderColor: '#000', mb: 1 }} />

            {/* Title */}
            <Box sx={{ textAlign: 'center', mb: 1 }}>
              <Typography sx={{ fontSize: 16, fontWeight: 'bold' }}>
                ADMISSION FORM ({yearStart}-{yearEnd})
              </Typography>
            </Box>

            {/* Form No & Class */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, fontSize: 11 }}>
              <Box sx={{ border: '1px solid #000', px: 1, py: 0.3, width: 130, height: 80, textAlign: 'center', fontSize: 10 }}>
                Paste<br/>Student's<br/>Photo Copy
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Box>Form No.<Box component="span" sx={uf}>{formNo}</Box></Box>
                <Box sx={{ mt: 0.5 }}>Admission sought in Class: <Box component="span" sx={uf}>{form.classAdmission}</Box></Box>
              </Box>
            </Box>

            {/* Student Details */}
            <Box sx={{ fontSize: 11, lineHeight: 2.2 }}>
              <Box>1) Student's Name <Box component="span" sx={{...uf, minWidth: 120}}>{form.surname}</Box> (Surname) <Box component="span" sx={{...uf, minWidth: 120}}>{form.firstName}</Box> (Name) <Box component="span" sx={{...uf, minWidth: 100}}>{form.middleName}</Box></Box>
              <Box>2) Father's Name <Box component="span" sx={{...uf, minWidth: 120}}>{form.fatherSurname}</Box> (Surname) <Box component="span" sx={{...uf, minWidth: 100}}>{form.fatherFirstName}</Box> (Name) <Box component="span" sx={{...uf, minWidth: 100}}>{form.fatherMiddleName}</Box> (Father's Name)</Box>
              <Box>3) Mother's Name <Box component="span" sx={{...uf, minWidth: 250}}>{form.motherName}</Box></Box>
              <Box>4) Religion <Box component="span" sx={{...uf, minWidth: 80}}>{form.religion}</Box> Caste/Sub Caste <Box component="span" sx={{...uf, minWidth: 120}}>{form.caste}</Box> Category <Box component="span" sx={{...uf, minWidth: 80}}>{form.category}</Box></Box>
              <Box>5) Date of Birth (In Figures): <Box component="span" sx={{...uf, minWidth: 30}}>{form.dobDate}</Box> / <Box component="span" sx={{...uf, minWidth: 30}}>{form.dobMonth}</Box> / <Box component="span" sx={{...uf, minWidth: 50}}>{form.dobYear}</Box></Box>
              <Box sx={{ pl: 2 }}>(In Words): <Box component="span" sx={{...uf, minWidth: 350}}>{form.dobWords}</Box></Box>
              <Box>6) Place of Birth <Box component="span" sx={{...uf, minWidth: 100}}>{form.birthPlace}</Box> (Taluka) <Box component="span" sx={{...uf, minWidth: 100}}>{form.birthTaluka}</Box> (District) <Box component="span" sx={{...uf, minWidth: 100}}>{form.birthDistrict}</Box></Box>
              <Box>7) Student Aadhar No. <Box component="span" sx={{...uf, minWidth: 150}}>{form.aadharNo}</Box> Blood Group <Box component="span" sx={{...uf, minWidth: 60}}>{form.bloodGroup}</Box></Box>
              <Box>8) Student's Mother Tongue: <Box component="span" sx={{...uf, minWidth: 100}}>{form.motherTongue}</Box> Nationality <Box component="span" sx={{...uf, minWidth: 100}}>{form.nationality}</Box></Box>
              <Box>9) Previous School & Board <Box component="span" sx={{...uf, minWidth: 300}}>{form.previousSchool}</Box></Box>
            </Box>

            {/* Declaration */}
            <Box sx={{ mt: 2, fontSize: 10, fontStyle: 'italic' }}>
              I hereby declare that the above mentioned details of my son/daughter/ward are correct
            </Box>

            {/* Parent Info */}
            <Box sx={{ fontSize: 11, lineHeight: 2.2, mt: 1 }}>
              <Box>Parent's Name <Box component="span" sx={{...uf, minWidth: 250}}>{form.parentName}</Box></Box>
              <Box>Address: <Box component="span" sx={{...uf, minWidth: 350}}>{form.parentAddress}</Box></Box>
              <Box>Contact No. <Box component="span" sx={{...uf, minWidth: 150}}>{form.contactNo}</Box></Box>
              <Box>Alternative Contact <Box component="span" sx={{...uf, minWidth: 150}}>{form.altContact}</Box></Box>
            </Box>

            {/* Signatures */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3, fontSize: 11 }}>
              <Box>Date: _______________</Box>
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ borderTop: '1px solid #000', width: 150, mb: 0.3 }} />
                (Signature of Parent)
              </Box>
            </Box>

            {/* Office Use */}
            <Box sx={{ mt: 3, border: '2px solid #000', p: 1.5, fontSize: 11 }}>
              <Typography sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: 12, mb: 1 }}>For office use only.</Typography>
              <Box>Master/Miss <Box component="span" sx={{...uf, minWidth: 150}}>{form.surname} {form.firstName}</Box></Box>
              <Box sx={{ mt: 0.5 }}>is given admission to Std. <Box component="span" sx={{...uf, minWidth: 60}}>{form.classAdmission}</Box> w.e.f. ___/___/20___ for the Academic Year 20{yearStart.slice(-2)}-20{yearEnd}</Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                <Box>SUPERVISOR</Box>
                <Box>PRINCIPAL</Box>
              </Box>
            </Box>

            {/* PAGE BREAK */}
            <Box sx={{ pageBreakBefore: 'always', pt: 3 }} />

            {/* PAGE 2: CERTIFICATE FROM PARENT */}
            <Typography sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: 13, mt: 2, mb: 1 }}>CERTIFICATE FROM THE PARENT</Typography>
            <Typography sx={{ fontSize: 10, lineHeight: 1.8, mb: 2 }}>
              I/We hereby certify that the above information provided by me/us is correct and I/We understand that
              if the information is found to be incorrect or false, the ward shall be automatically debarred from
              selection /admission process without any correspondence in this regard. I/We accept the process of
              admission undertaken by the school and I/We will abide by the decision taken by the school authorities.
            </Typography>
            <Box sx={{ fontSize: 11, lineHeight: 2 }}>
              <Box>Signature of Parent – _______________</Box>
              <Box>Name – <Box component="span" sx={{...uf, minWidth: 250}}>{form.parentName}</Box></Box>
            </Box>

            <Box sx={{ mt: 3, border: '2px solid #000', p: 1.5, fontSize: 11 }}>
              <Typography sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: 12, mb: 1 }}>For office use only</Typography>
              <Box>Remark -Registration done for _______________</Box>
              <Box>Date - ___/___/___</Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                <Box><strong>Office Sign.</strong><br/>Office Seal.</Box>
                <Box><strong>Principal Sign.</strong></Box>
              </Box>
            </Box>

            {/* PAGE BREAK */}
            <Box sx={{ pageBreakBefore: 'always', pt: 3 }} />

            {/* PAGE 3: REGISTRATION FORM */}
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 'bold' }}>Vidya Pratishthan's</Typography>
              <Typography sx={{ fontSize: 16, fontWeight: 'bold' }}>Supe English Medium School (SSC)</Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 'bold' }}>REGISTRATION FORM 20{yearStart.slice(-2)}-20{yearEnd}</Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, fontSize: 11 }}>
              <Box>Date of Submission - ___/___/20___</Box>
              <Box>Class - <Box component="span" sx={uf}>{form.classAdmission}</Box></Box>
              <Box>Reg. Form No. - <Box component="span" sx={uf}>{formNo}</Box></Box>
            </Box>

            <Typography sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: 12, mb: 1.5, textDecoration: 'underline' }}>PARENT INFORMATION</Typography>

            <Grid container spacing={4}>
              <Grid item xs={6}>
                <Box sx={{ border: '1px solid #000', height: 80, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10 }}>
                  Paste Father Photo
                </Box>
                <Box sx={{ fontSize: 11, lineHeight: 2 }}>
                  <Box>1) Name of Father - <Box component="span" sx={uf}>{form.fatherFirstName} {form.fatherSurname}</Box></Box>
                  <Box>Occupation - <Box component="span" sx={uf}>{form.fatherOccupation}</Box></Box>
                  <Box>2) Mobile No - <Box component="span" sx={uf}>{form.fatherMobile}</Box></Box>
                  <Box>3) E-Mail ID - <Box component="span" sx={uf}>{form.fatherEmail}</Box></Box>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ border: '1px solid #000', height: 80, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10 }}>
                  Paste Mother Photo
                </Box>
                <Box sx={{ fontSize: 11, lineHeight: 2 }}>
                  <Box>1) Name of Mother - <Box component="span" sx={uf}>{form.motherName}</Box></Box>
                  <Box>Occupation - <Box component="span" sx={uf}>{form.motherOccupation}</Box></Box>
                  <Box>2) Mobile No - <Box component="span" sx={uf}>{form.motherMobile}</Box></Box>
                  <Box>3) E-Mail ID - <Box component="span" sx={uf}>{form.motherEmail}</Box></Box>
                </Box>
              </Grid>
            </Grid>

            <Box sx={{ mt: 2, fontSize: 11 }}>
              <Box>• If the Student has any kind of illness – <Box component="span" sx={{...uf, minWidth: 200}}>{form.illness}</Box></Box>
            </Box>

            <Box sx={{ mt: 2, fontSize: 11 }}>
              <Typography sx={{ fontWeight: 'bold', fontSize: 11, mb: 0.5 }}>Note – Documents to Submit (Attested copies)</Typography>
              <Box>1) Original Birth Certificate ☐ OR School Leaving Certificate ☐</Box>
              <Box>2) Aadhar Xerox: Student ☐ Father ☐ Mother ☐</Box>
              <Box>3) Attested Caste Certificate ☐ OR L.C. of Parent ☐</Box>
              <Box>4) Student's Recent Report card ☐</Box>
              <Box>5) Passport size photograph Father 01 ☐ Mother 01 ☐ Student 03 ☐</Box>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Enroll Student Dialog */}
      <Dialog open={enrollDialog} onClose={() => setEnrollDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Enroll Student from Admission Form</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            This will create a student record using the admission form data.
          </Alert>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>Student:</strong> {form.surname} {form.firstName} {form.middleName}<br/>
            <strong>Father:</strong> {form.fatherSurname} {form.fatherFirstName}<br/>
            <strong>DOB:</strong> {form.dobDate}/{form.dobMonth}/{form.dobYear}
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Select Class</InputLabel>
            <Select value={enrollClass} label="Select Class" onChange={e => handleClassChange(e.target.value)}>
              {classes.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Select Section</InputLabel>
            <Select value={enrollSection} label="Select Section" onChange={e => setEnrollSection(e.target.value)} disabled={!enrollClass}>
              {sections.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEnrollDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEnroll} disabled={loading}
            sx={{ background: 'linear-gradient(135deg,#4caf50,#2e7d32)' }}>
            {loading ? <CircularProgress size={20} /> : 'Enroll Student'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdmissionForm;
